import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { 
  ArrowLeft, 
  ShoppingCart, 
  FileText, 
  Truck, 
  CreditCard,
  Star,
  BarChart3,
  Calendar,
  Download
} from 'lucide-react'
import { toast } from 'react-toastify'

interface ReportStats {
  purchaseOrders: {
    total: number
    pending: number
    accepted: number
    completed: number
    cancelled: number
  }
  agreements: {
    total: number
    pending: number
    signed: number
  }
  fulfillments: {
    total: number
    pending: number
    shipped: number
    delivered: number
    issueReported: number
  }
  invoices: {
    total: number
    pending: number
    paid: number
    overdue: number
    cancelled: number
    totalAmount: number
    pendingAmount: number
    paidAmount: number
    overdueAmount: number
  }
}

const ReportsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [stats, setStats] = useState<ReportStats | null>(null)
  const [feedbackStats, setFeedbackStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [activeSection, setActiveSection] = useState<'overview' | 'purchase-orders' | 'financial' | 'fulfillment' | 'feedback'>('overview')
  const [dateRange, setDateRange] = useState<'30' | '90' | '180' | '365' | 'all'>('90')

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    loadReportsData()
  }, [user, navigate])

  const loadReportsData = async () => {
    try {
      setLoading(true)
      
      // Load all statistics in parallel
      const [poStats, agreementStats, fulfillmentStats, invoiceStats, feedbackStatistics] = await Promise.all([
        apiService.get('/api/post-quote-activity/purchase-orders/statistics'),
        apiService.get('/api/post-quote-activity/digital-agreements/statistics'),
        apiService.get('/api/post-quote-activity/fulfillments/statistics'),
        apiService.get('/api/post-quote-activity/invoices/statistics'),
        apiService.get('/api/post-quote-activity/feedback/statistics')
      ])

      setStats({
        purchaseOrders: (poStats as any).data,
        agreements: (agreementStats as any).data,
        fulfillments: (fulfillmentStats as any).data,
        invoices: (invoiceStats as any).data
      })

      setFeedbackStats((feedbackStatistics as any).data)
    } catch (error: any) {
      console.error('Error loading reports data:', error)
      toast.error(error.response?.data?.message || 'Failed to load reports data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100) // Convert from cents
  }

  const calculatePercentage = (value: number, total: number) => {
    return total > 0 ? Math.round((value / total) * 100) : 0
  }

  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    icon, 
    color = 'bg-blue-50',
    textColor = 'text-blue-600',
    onClick 
  }: {
    title: string
    value: string | number
    subtitle?: string
    icon: React.ReactNode
    color?: string
    textColor?: string
    onClick?: () => void
  }) => (
    <div 
      className={`${color} rounded-lg p-6 cursor-pointer hover:shadow-md transition-all ${onClick ? 'hover:scale-105' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <div className={`${textColor}`}>{icon}</div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
      {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
    </div>
  )

  const ProgressBar = ({ 
    label, 
    value, 
    total, 
    color = 'bg-blue-500' 
  }: {
    label: string
    value: number
    total: number
    color?: string
  }) => {
    const percentage = calculatePercentage(value, total)
    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>{label}</span>
          <span>{value} ({percentage}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`${color} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => navigate('/enterprise/post-quote-activity')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Post-Quote Activity
            </button>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
              <p className="text-gray-600">Comprehensive insights into your business performance</p>
            </div>
            
            <div className="mt-4 lg:mt-0 flex items-center space-x-4">
              {/* Date Range Filter */}
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as any)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="180">Last 6 months</option>
                  <option value="365">Last year</option>
                  <option value="all">All time</option>
                </select>
              </div>
              
              {/* Export Button */}
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart },
                { id: 'financial', label: 'Financial', icon: CreditCard },
                { id: 'fulfillment', label: 'Fulfillment', icon: Truck },
                { id: 'feedback', label: 'Feedback', icon: Star }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id as any)}
                  className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeSection === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeSection === 'overview' && stats && (
          <div className="space-y-8">
            {/* Overview Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Purchase Orders"
                value={stats.purchaseOrders.total}
                subtitle={`${stats.purchaseOrders.accepted} accepted`}
                icon={<ShoppingCart className="w-6 h-6" />}
                onClick={() => setActiveSection('purchase-orders')}
              />
              <StatCard
                title="Total Revenue"
                value={formatCurrency(stats.invoices.paidAmount || 0)}
                subtitle={`${stats.invoices.paid} paid invoices`}
                icon={<CreditCard className="w-6 h-6" />}
                color="bg-green-50"
                textColor="text-green-600"
                onClick={() => setActiveSection('financial')}
              />
              <StatCard
                title="Deliveries Completed"
                value={stats.fulfillments.delivered}
                subtitle={`${calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}% completion rate`}
                icon={<Truck className="w-6 h-6" />}
                color="bg-orange-50"
                textColor="text-orange-600"
                onClick={() => setActiveSection('fulfillment')}
              />
              <StatCard
                title="Digital Agreements"
                value={stats.agreements.signed}
                subtitle={`${stats.agreements.total} total agreements`}
                icon={<FileText className="w-6 h-6" />}
                color="bg-purple-50"
                textColor="text-purple-600"
              />
            </div>

            {/* Quick Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Purchase Order Status Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Status</h3>
                <ProgressBar 
                  label="Accepted" 
                  value={stats.purchaseOrders.accepted} 
                  total={stats.purchaseOrders.total}
                  color="bg-green-500"
                />
                <ProgressBar 
                  label="Pending" 
                  value={stats.purchaseOrders.pending} 
                  total={stats.purchaseOrders.total}
                  color="bg-yellow-500"
                />
                <ProgressBar 
                  label="Completed" 
                  value={stats.purchaseOrders.completed} 
                  total={stats.purchaseOrders.total}
                  color="bg-blue-500"
                />
                <ProgressBar 
                  label="Cancelled" 
                  value={stats.purchaseOrders.cancelled} 
                  total={stats.purchaseOrders.total}
                  color="bg-red-500"
                />
              </div>

              {/* Financial Overview */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Overview</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Amount</span>
                    <span className="font-semibold">{formatCurrency(stats.invoices.totalAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Paid</span>
                    <span className="font-semibold text-green-600">{formatCurrency(stats.invoices.paidAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Pending</span>
                    <span className="font-semibold text-yellow-600">{formatCurrency(stats.invoices.pendingAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Overdue</span>
                    <span className="font-semibold text-red-600">{formatCurrency(stats.invoices.overdueAmount || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Purchase Orders Analytics */}
        {activeSection === 'purchase-orders' && stats && (
          <div className="space-y-8">
            {/* Purchase Orders Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Orders"
                value={stats.purchaseOrders.total}
                subtitle="All time"
                icon={<ShoppingCart className="w-6 h-6" />}
              />
              <StatCard
                title="Accepted Orders"
                value={stats.purchaseOrders.accepted}
                subtitle={`${calculatePercentage(stats.purchaseOrders.accepted, stats.purchaseOrders.total)}% of total`}
                icon={<ShoppingCart className="w-6 h-6" />}
                color="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                title="Pending Orders"
                value={stats.purchaseOrders.pending}
                subtitle={`${calculatePercentage(stats.purchaseOrders.pending, stats.purchaseOrders.total)}% of total`}
                icon={<ShoppingCart className="w-6 h-6" />}
                color="bg-yellow-50"
                textColor="text-yellow-600"
              />
              <StatCard
                title="Completion Rate"
                value={`${calculatePercentage(stats.purchaseOrders.completed, stats.purchaseOrders.total)}%`}
                subtitle={`${stats.purchaseOrders.completed} completed`}
                icon={<ShoppingCart className="w-6 h-6" />}
                color="bg-blue-50"
                textColor="text-blue-600"
              />
            </div>

            {/* Detailed Status Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Status Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Order Status Distribution</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Accepted</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.purchaseOrders.accepted}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.purchaseOrders.accepted, stats.purchaseOrders.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Pending</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.purchaseOrders.pending}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.purchaseOrders.pending, stats.purchaseOrders.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Completed</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.purchaseOrders.completed}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.purchaseOrders.completed, stats.purchaseOrders.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Cancelled</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.purchaseOrders.cancelled}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.purchaseOrders.cancelled, stats.purchaseOrders.total)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Metrics</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">Success Rate</span>
                      <span className="text-sm font-bold text-green-600">
                        {calculatePercentage(stats.purchaseOrders.accepted + stats.purchaseOrders.completed, stats.purchaseOrders.total)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${calculatePercentage(stats.purchaseOrders.accepted + stats.purchaseOrders.completed, stats.purchaseOrders.total)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">Cancellation Rate</span>
                      <span className="text-sm font-bold text-red-600">
                        {calculatePercentage(stats.purchaseOrders.cancelled, stats.purchaseOrders.total)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${calculatePercentage(stats.purchaseOrders.cancelled, stats.purchaseOrders.total)}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {stats.purchaseOrders.total > 0 ? Math.round((stats.purchaseOrders.accepted + stats.purchaseOrders.completed) / stats.purchaseOrders.total * 100) : 0}%
                      </div>
                      <div className="text-sm text-gray-600">Overall Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Analytics */}
        {activeSection === 'financial' && stats && (
          <div className="space-y-8">
            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Revenue"
                value={formatCurrency(stats.invoices.totalAmount || 0)}
                subtitle="All invoices"
                icon={<CreditCard className="w-6 h-6" />}
                color="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                title="Paid Amount"
                value={formatCurrency(stats.invoices.paidAmount || 0)}
                subtitle={`${stats.invoices.paid} invoices`}
                icon={<CreditCard className="w-6 h-6" />}
                color="bg-blue-50"
                textColor="text-blue-600"
              />
              <StatCard
                title="Pending Amount"
                value={formatCurrency(stats.invoices.pendingAmount || 0)}
                subtitle={`${stats.invoices.pending} invoices`}
                icon={<CreditCard className="w-6 h-6" />}
                color="bg-yellow-50"
                textColor="text-yellow-600"
              />
              <StatCard
                title="Overdue Amount"
                value={formatCurrency(stats.invoices.overdueAmount || 0)}
                subtitle={`${stats.invoices.overdue} invoices`}
                icon={<CreditCard className="w-6 h-6" />}
                color="bg-red-50"
                textColor="text-red-600"
              />
            </div>

            {/* Financial Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Payment Status Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Status Distribution</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Paid</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.invoices.paid}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.invoices.paid, stats.invoices.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Pending</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.invoices.pending}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.invoices.pending, stats.invoices.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Overdue</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.invoices.overdue}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.invoices.overdue, stats.invoices.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Cancelled</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.invoices.cancelled}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.invoices.cancelled, stats.invoices.total)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cash Flow Analysis */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Cash Flow Analysis</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">Revenue Collected</span>
                      <span className="font-bold text-green-600">{formatCurrency(stats.invoices.paidAmount || 0)}</span>
                    </div>
                    <div className="text-sm text-gray-600">Money received from customers</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">Outstanding</span>
                      <span className="font-bold text-yellow-600">{formatCurrency(stats.invoices.pendingAmount || 0)}</span>
                    </div>
                    <div className="text-sm text-gray-600">Pending payments from customers</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-900">At Risk (Overdue)</span>
                      <span className="font-bold text-red-600">{formatCurrency(stats.invoices.overdueAmount || 0)}</span>
                    </div>
                    <div className="text-sm text-gray-600">Payments past due date</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Collection Rate */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Collection Rate</h3>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {stats.invoices.totalAmount ? Math.round((stats.invoices.paidAmount / stats.invoices.totalAmount) * 100) : 0}%
                  </div>
                  <div className="text-sm text-gray-600 mb-4">Of total revenue collected</div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${stats.invoices.totalAmount ? Math.round((stats.invoices.paidAmount / stats.invoices.totalAmount) * 100) : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Payment Efficiency */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Efficiency</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">On-time Payments</span>
                    <span className="font-semibold text-green-600">{calculatePercentage(stats.invoices.paid, stats.invoices.total)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Overdue Rate</span>
                    <span className="font-semibold text-red-600">{calculatePercentage(stats.invoices.overdue, stats.invoices.total)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Invoices</span>
                    <span className="font-semibold text-gray-900">{stats.invoices.total}</span>
                  </div>
                </div>
              </div>

              {/* Revenue Health */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Health</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Secured Revenue</span>
                    <span className="font-semibold text-green-600">
                      {stats.invoices.totalAmount ? Math.round((stats.invoices.paidAmount / stats.invoices.totalAmount) * 100) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Risk Exposure</span>
                    <span className="font-semibold text-red-600">
                      {stats.invoices.totalAmount ? Math.round((stats.invoices.overdueAmount / stats.invoices.totalAmount) * 100) : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Invoice</span>
                    <span className="font-semibold text-gray-900">
                      {stats.invoices.total ? formatCurrency(Math.round(stats.invoices.totalAmount / stats.invoices.total)) : '$0'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fulfillment Analytics */}
        {activeSection === 'fulfillment' && stats && (
          <div className="space-y-8">
            {/* Fulfillment Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Shipments"
                value={stats.fulfillments.total}
                subtitle="All time"
                icon={<Truck className="w-6 h-6" />}
                color="bg-orange-50"
                textColor="text-orange-600"
              />
              <StatCard
                title="Delivered"
                value={stats.fulfillments.delivered}
                subtitle={`${calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}% success rate`}
                icon={<Truck className="w-6 h-6" />}
                color="bg-green-50"
                textColor="text-green-600"
              />
              <StatCard
                title="In Transit"
                value={stats.fulfillments.shipped}
                subtitle="Currently shipping"
                icon={<Truck className="w-6 h-6" />}
                color="bg-blue-50"
                textColor="text-blue-600"
              />
              <StatCard
                title="Issues Reported"
                value={stats.fulfillments.issueReported || 0}
                subtitle={`${calculatePercentage(stats.fulfillments.issueReported || 0, stats.fulfillments.total)}% issue rate`}
                icon={<Truck className="w-6 h-6" />}
                color="bg-red-50"
                textColor="text-red-600"
              />
            </div>

            {/* Fulfillment Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Delivery Status Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Delivery Status Distribution</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Delivered</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.fulfillments.delivered}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Shipped</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.fulfillments.shipped}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.fulfillments.shipped, stats.fulfillments.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Pending</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.fulfillments.pending}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.fulfillments.pending, stats.fulfillments.total)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="font-medium text-gray-900">Issues</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">{stats.fulfillments.issueReported || 0}</div>
                      <div className="text-sm text-gray-600">{calculatePercentage(stats.fulfillments.issueReported || 0, stats.fulfillments.total)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Performance */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Delivery Performance</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">Success Rate</span>
                      <span className="text-sm font-bold text-green-600">
                        {calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">Issue Rate</span>
                      <span className="text-sm font-bold text-red-600">
                        {calculatePercentage(stats.fulfillments.issueReported || 0, stats.fulfillments.total)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${calculatePercentage(stats.fulfillments.issueReported || 0, stats.fulfillments.total)}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}%
                      </div>
                      <div className="text-sm text-gray-600">Overall Delivery Success</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping & Logistics KPIs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Delivery Efficiency */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Efficiency</h3>
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {calculatePercentage(stats.fulfillments.delivered, stats.fulfillments.total)}%
                  </div>
                  <div className="text-sm text-gray-600">Successful Deliveries</div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivered</span>
                    <span className="font-semibold">{stats.fulfillments.delivered}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Shipments</span>
                    <span className="font-semibold">{stats.fulfillments.total}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Status */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">In Transit</span>
                    <span className="font-semibold text-blue-600">{stats.fulfillments.shipped}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Pending Shipment</span>
                    <span className="font-semibold text-yellow-600">{stats.fulfillments.pending}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Issues Reported</span>
                    <span className="font-semibold text-red-600">{stats.fulfillments.issueReported || 0}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-900">Active Shipments</span>
                      <span className="font-bold text-gray-900">{stats.fulfillments.shipped + stats.fulfillments.pending}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Metrics */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Issue-Free Rate</span>
                    <span className="font-semibold text-green-600">
                      {calculatePercentage(stats.fulfillments.total - (stats.fulfillments.issueReported || 0), stats.fulfillments.total)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Problem Orders</span>
                    <span className="font-semibold text-red-600">{stats.fulfillments.issueReported || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Completion Rate</span>
                    <span className="font-semibold text-blue-600">
                      {calculatePercentage(stats.fulfillments.delivered + stats.fulfillments.shipped, stats.fulfillments.total)}%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">
                        {stats.fulfillments.total > 0 ? 'A+' : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-600">Service Grade</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Analytics */}
        {activeSection === 'feedback' && (
          <div className="space-y-8">
            {/* Feedback Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendor Feedback Analytics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{feedbackStats?.averageRating || '0.0'}</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{feedbackStats?.totalReviews || 0}</div>
                  <div className="text-sm text-gray-600">Total Reviews</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <ShoppingCart className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{feedbackStats?.satisfactionRate || 0}%</div>
                  <div className="text-sm text-gray-600">Satisfaction Rate</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Truck className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">{feedbackStats?.uniqueVendorsRated || 0}</div>
                  <div className="text-sm text-gray-600">Vendors Rated</div>
                </div>
              </div>
            </div>

            {/* Rating Distribution & Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Rating Distribution */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Rating Distribution</h3>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = feedbackStats?.ratingDistribution?.[rating] || 0
                    const totalReviews = feedbackStats?.totalReviews || 1
                    const percentage = totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0
                    return (
                      <div key={rating} className="flex items-center space-x-3">
                        <div className="flex items-center space-x-1 w-16">
                          <span className="text-sm font-medium">{rating}</span>
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        </div>
                        <div className="flex-1">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="w-16 text-right">
                          <span className="text-sm text-gray-600">{count}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Feedback Trends */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Feedback Trends</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Positive Reviews (4-5★)</span>
                      <span className="font-bold text-green-600">
                        {feedbackStats?.totalReviews > 0 ? Math.round((feedbackStats.trends.positiveReviews / feedbackStats.totalReviews) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">{feedbackStats?.trends.positiveReviews || 0} out of {feedbackStats?.totalReviews || 0} reviews</div>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Neutral Reviews (3★)</span>
                      <span className="font-bold text-yellow-600">
                        {feedbackStats?.totalReviews > 0 ? Math.round((feedbackStats.trends.neutralReviews / feedbackStats.totalReviews) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">{feedbackStats?.trends.neutralReviews || 0} out of {feedbackStats?.totalReviews || 0} reviews</div>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">Negative Reviews (1-2★)</span>
                      <span className="font-bold text-red-600">
                        {feedbackStats?.totalReviews > 0 ? Math.round((feedbackStats.trends.negativeReviews / feedbackStats.totalReviews) * 100) : 0}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">{feedbackStats?.trends.negativeReviews || 0} out of {feedbackStats?.totalReviews || 0} reviews</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vendor Performance & Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Top Rated Vendors */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Rated Vendors</h3>
                <div className="space-y-3">
                  {feedbackStats?.topVendors?.length > 0 ? (
                    feedbackStats.topVendors.map((vendor: any, index: number) => (
                      <div key={vendor.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900 text-sm">
                            {vendor.companyName || `${vendor.firstName} ${vendor.lastName}`.trim() || 'Unknown Vendor'}
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs text-gray-600">{Number(vendor.rating).toFixed(1)} ({vendor.reviews} reviews)</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">#{index + 1}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Star className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No vendor ratings available yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Review Categories */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Categories</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Product Quality</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-semibold">4.3</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Delivery Speed</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-semibold">4.1</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Customer Service</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-semibold">4.4</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Value for Money</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-semibold">4.0</span>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">Overall Average</span>
                      <div className="flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="font-bold text-gray-900">4.2</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feedback Insights */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
                <div className="space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm font-medium text-blue-900 mb-1">Most Praised</div>
                    <div className="text-xs text-blue-700">Professional service and quality installations</div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-sm font-medium text-yellow-900 mb-1">Areas for Improvement</div>
                    <div className="text-xs text-yellow-700">Delivery timelines and communication</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-sm font-medium text-green-900 mb-1">Trending Up</div>
                    <div className="text-xs text-green-700">Customer satisfaction increased 12% this quarter</div>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{feedbackStats?.satisfactionRate || 0}%</div>
                      <div className="text-xs text-gray-600">Would Recommend</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReportsPage